// src/stores/map/hooks/useUpdateLayers.ts
//
// Debounced layer setter that replaces the Redux thunk `updateLayers`.
// Debounce window: 500 ms (matches the original Redux implementation).
import { useCallback, useEffect, useMemo } from "react";

import _debounce from "lodash/debounce";
import type { MapLayer } from "types/maps/entities";

import { useMapStore } from "../mapStore";

/**
 * Returns a 500 ms debounced layer setter, replacing the Redux thunk
 * `updateLayers`. The returned function is stable across renders (assuming
 * the store action reference is stable, which Zustand guarantees).
 */
export function useUpdateLayers(): (layers: MapLayer[]) => void {
  const setLayers = useMapStore((s) => s.setLayers);

  const debouncedSetLayers = useMemo(
    () => _debounce((layers: MapLayer[]) => setLayers(layers), 500),
    [setLayers]
  );

  useEffect(() => () => debouncedSetLayers.cancel(), [debouncedSetLayers]);

  return useCallback(
    (layers: MapLayer[]) => {
      debouncedSetLayers(layers);
    },
    [debouncedSetLayers]
  );
}
